import { useCallback, useState } from 'react';

import { Typography } from '@mui/material';
import { selectCurrentEntityType } from 'common/components/deleteDialog/deleteDialogSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserProfileForeignSystems } from 'common/services/api/UserProfileForeignSystems/UserProfileForeignSystems.thunk';
import { UserDetails, UserForeignKey } from 'common/types/Entities';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { DeleteDialogContentProps, DrawerEntitiesNames } from '../../common/types/TypeDefs';

import { setUserProfileForeignKeys } from './userProfileForeignSystemsSlice';

export function UserProfileForeignSystemsDeleteDialog({
    editingRow
}: Readonly<DeleteDialogContentProps<UserForeignKey | null>>) {
    const currentDialogEntity = useAppSelector(selectCurrentEntityType);
    const { foreignSystemId, foreignSystemName, userId, foreignKey } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const successHandler = () => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Foreign System has been deleted.', { variant: 'success' });
        setProcessing(false);
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        handleDeleteDialogError('Foreign System', enqueueSnackbar, extractStatusCode(error.message));
        setProcessing(false);
    };

    const userDetails = useAppSelector(selectUserDetails);
    const getUserFullName = (userDetailsProp: UserDetails | null): string => {
        const UNKNOWN_USER_DEFAULT_FULL_NAME = 'Unknown User';
        const { firstName='', lastName='' } = userDetailsProp ?? {};
        const existingUserFullName = `${firstName} ${lastName}`;
        return firstName && lastName ? existingUserFullName : UNKNOWN_USER_DEFAULT_FULL_NAME;
    };

    const foreignSystemDeleteCallback = useCallback(() => {
        if (foreignSystemId) {
            setProcessing(true);
            dispatch(deleteUserProfileForeignSystems({
                userId: userId ?? '',
                foreignSystemId: foreignSystemId || '',
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setUserProfileForeignKeys(result.rows));
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                })
                .finally(()=> {
                    setProcessing(false);
                });
        }
    }, [foreignSystemId]);

    if (currentDialogEntity !== DrawerEntitiesNames.UserForeignSystems) {
        return null;
    }

    return  (
        <DeleteDialog deleteCallback={foreignSystemDeleteCallback} processing={processing ?? false} title={'Foreign System link'}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Name: <Typography component="span" color="text.primary" fontWeight="400">{getUserFullName(userDetails)}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Foreign System: <Typography component="span" color="text.primary" fontWeight="400">{foreignSystemName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Foreign System Key: <Typography component="span" color="text.primary" fontWeight="400">{foreignKey}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
